import type { MetaFunction, LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, redirect, useLoaderData } from '@remix-run/react'
import { prisma } from '#app/utils/db.server.js'
import { buildRatingMeter, cleanSkillName } from '#app/utils/scripts.helper.js'
import { formatDateTimeFromString, momentsAgo, prettyDateShort } from '#app/utils/dateFormat.js'
import { getServerBySlug } from '#app/utils/shards.server.js'
import { getBuildAndRelatedByServerAndSlug } from '#app/utils/builds.server.js'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    const serverDetails = data?.build?.server;
    const title = `UO Template | ${data?.build?.name} | UO Addicts`;
    const description = data?.build?.description ?? "Check out this build for Ultima Online on UO Addicts";
    return [
    { title: title},
    { name: "description", content: description },
    { name: "keywords", content: `builds,templates,uo,ultima online,shard,server,${serverDetails?.name}`},
    { property: "og:image", content: `https://uoaddicts.com/api/build-meta-image/${data?.build?.buildSignature}`},
    { property: "og:image:alt", content: "Build details for Ultima Online on UO Addicts"},
    { property: "twitter:card", content: "summary_large_image"},
    { property: "twitter:creator", content: "@jaseowns"},
    { property: "twitter:site", content: "@jaseowns"},
    { property: "og:title", content: title},
    { property: "og:description", content: description},
]}



export async function loader({ request, params }: LoaderFunctionArgs) {
    const serverDetails = await getServerBySlug({slug: params.serverSlug || ""})
    if (!serverDetails) {
        return redirect('/builds');
    }  
    const buildDetails = await getBuildAndRelatedByServerAndSlug({serverId: serverDetails.id, slug: params.buildId || ""});
    return json({
        build: buildDetails.thisBuild,
        related: buildDetails.builds
    });
};

export default function SpecificBuildRoute() {
  const { build, related } = useLoaderData<typeof loader>()
  let total = 0;

  if (!build) {
    return <div>Build not found</div>
  }

  const buildBreakout = build?.build?.split(",").map(x => { 
    const skill = x.split("|")
    const skillName = skill[0] ?? "No skill name"
    const skillValue = Number(skill[1]) ?? 0
    total += skillValue
    return { name: skillName, value: skillValue }
  })

return (
	<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
		<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">Ultima Online Build for {build.server.name}</h1>
		</div>
		<p className="mt-2 text-center text-muted-foreground">
			This build was uploaded {momentsAgo(build.createdAt)} ago by {build.user.username} on {prettyDateShort(build.createdAt)}. Do you want to <Link to={`/builds/${build.server.slug}/new`} className='underline hover:no-underline'>add your own?</Link>
		</p>
		<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
			<div className="flex flex-col md:flex-row">


            <div>
                {/* {build.description} 
                {build.difficulty} 
                {build.gear} 
                {build.taggables.map((taggable, index) => { return taggable.tag.name})}
                {build.characterId} */}
                {/* {build.name} {build.build} {build.buildSignature} */}

                  <div className="skill-scroll-container">
                        <div className='m-4'>
                            <div className='text-center m-auto min-h-32 align-text-bottom'>
                            <Link to={`/builds/${build.server.slug}`}><img src={build.server.logo} alt={build.server.name} className="max-h-24 server-logo m-auto" /></Link>
                            by <Link to={`/users/${build.user.username}/builds`} className="underline hover:no-underline">{build.user.username}</Link> {momentsAgo(build.createdAt.toString())} ago
                            </div>
           
                            <div className="skill-container-top">
                                <div className='not-input'>{build.name.length ? build.name : "No build name"}</div>
                            </div>
                            <div className="skill-container skill-container-mid">
                                {buildBreakout?.sort( (a,b) => {
                                    if (a.name > b.name) {
                                        return 1;
                                    } else if (b.name > a.name) {
                                        return -1;
                                    }
                                    return 0;
                                }).map(x => { return <div className="skill-row" key={"selected_" + x.name}>
                                    <span className="name">{cleanSkillName(x.name)}</span> <span className="value">{x.value}</span>
                                    </div>})
                                }
                                <div className="skill-row totals">
                                    <span className="name">Total</span> <span className={total > 720 ? "value invalid" : "value"}>{total}</span>
                                </div>
                            </div>
                            <div className="skill-container-bot">&nbsp;</div>
                        </div>
                      </div>
              </div>

              <div>
                <h3 className='text-3xl my-4'>Notes from {build.user.username}:</h3>
                <p>Name: {build.name ? build.name : "No build name"}</p>
                <div className='w-full p-4'>
                                    <div className='flex justify-between text-sm mb-2'>
                                        <span>EASY</span>
                                        <span>HARDCORE</span>
                                    </div>
                                    <input type="range" min="0" max="10" disabled defaultValue={build.difficulty} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"/>
                                    <div className='text-center mt-2'>
                                        Newbie Friendliness: {buildRatingMeter(build.difficulty)}
                                    </div>
                                </div>
                <p>Description: {build.description ? build.description : "No description"}</p>
                <p>Gear: {build.gear ? build.gear : "No gear requirements"}</p>
                
                <h3 className='text-3xl my-4'>Build related data:</h3>
                <p>This build has been uploaded <b>{related.length} time{related.length != 1 ? "s" : ""}</b> on this shard.</p>
                <p>This build has been tagged with: {related.map(x => x.taggables.map((taggable, index) => { return <Link key={`tag_${index}`} className='underline hover:no-underline p-4' to={`/builds/tags/${taggable.tag.name}`}>{taggable.tag.name}</Link>}))}</p>
                <p className='text-muted-foreground my-8'>{build.buildSignature}</p>

                <Link to={`/builds/${build.server.slug}/new`} className='underline hover:no-underline text-2xl'><h3>Help UOAddicts Grow by Adding Your Build</h3></Link>
                {/* <p>Share this build on social media:</p>
                <div className='flex flex-row'>
                    <a href={`https://twitter.com/intent/tweet?text=Check out this Ultima Online build on UO Addicts: ${build.name} ${build.buildSignature}`} target="_blank" rel="noreferrer" className='m-2'>
                        <img src='/twitter.svg' alt='Twitter' className='w-8 h-8' />
                    </a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=https://uoaddicts.com/builds/${build.server.slug}/${build.slug}`} target="_blank" rel="noreferrer" className='m-2'>
                        <img src='/facebook.svg' alt='Facebook' className='w-8 h-8' />
                    </a>
                </div> */}
              </div>
			</div>
		</div>

	</div>
);
}
